import { gql, useQuery } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import Avatar from '@orn/shared-web/components/Avatar';
import Badge from '@orn/shared-web/components/Badge';
import { CalendarIcon } from '@orn/shared-web/icons';
import { billDisplayName } from '@orn/shared-web/lib/bill/billDisplayName';
import formatAccountLabel from '@orn/shared-web/utils/account/formatAccountLabel';
import { bankLogoSrc } from '@orn/shared-web/utils/bankLogoSrc';
import { useParams } from 'react-router-dom';

import { BillStatus } from '../../__generated__/globalTypes';
import Entity from '../../components/entity';
import { entityLogoSrc } from '../../components/entity/EntityAvatar/EntityAvatar';
import RecordAlertBanner from '../../components/entity/RecordAlertBanner';
import RecordPane, { useCloseRecord, type IRecordHeroMetaCell } from '../../components/entity/RecordPane';
import AttachmentsSection from '../../components/records/AttachmentsSection';
import MerchantSpendingSection from '../../components/records/MerchantSpendingSection';
import RecentTransactionsSection from '../../components/records/RecentTransactionsSection';
import RecurringStreamSection, { RecurringStreamSectionBase } from '../../components/records/RecurringStreamSection';
import { JsonDebugRow } from '../../components/records/rows';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import { PATHS } from '../../paths';
import { billDateCellValue, billRecordStatus, billPaymentsAtMerchant } from '../../utils/bill/billRecord';
import { toCurrency } from '../../utils/formatting';
import { getTimingPill } from '../../utils/records/timingPill';

import type { BillPageBillFragment, GetBillPageQuery, GetBillPageQueryVariables } from './__generated__/BillPane.gql';

export const BillPageBillFragmentDocument = gql`
  fragment BillPageBill on Bill {
    id
    status
    providerName
    amountDue
    currencyCode
    dueDate
    senderIdentifier
    rawJson @include(if: $includeRawJson)
    merchant {
      id
      name
      ...EntityAvatar
    }
    financialAccount {
      id
      name
      mask
      ...EntityAvatar
    }
    transactions {
      id
      date
      amount
      merchant {
        id
      }
    }
    recurringTransaction {
      id
      ...RecurringStreamSection
    }
    workItem {
      id
      ...RecordAlertBanner
    }
    ...AttachmentsSectionBill
  }
  ${Entity.Avatar.Fragment}
  ${RecurringStreamSectionBase.Fragment}
  ${RecordAlertBanner.Fragment}
  ${AttachmentsSection.Fragment}
`;

export const GET_BILL_PAGE = gql`
  query GetBillPage($id: ID!, $includeRawJson: Boolean!) {
    getBill(id: $id) {
      ...BillPageBill
    }
  }
  ${BillPageBillFragmentDocument}
`;

export type BillPageBill = BillPageBillFragment;

interface IBillPaneProps {
  /** Stories pass this directly; production rendering falls back to the route param. */
  billId?: string;
}

/**
 * The bill record page at `/bills/:billId`: the record shell over one
 * `getBill` read. A stream-linked bill shows the stream's Bill Overview where
 * a one-time bill shows Spending at its merchant; the rest of the body is the
 * same. Not behind the obligations gate — a bill is reachable from an
 * assistant chip regardless of it. The raw JSON is selected, and its row
 * drawn, only when `FeatureGate.DebugTransactionDetails` is on.
 */
const BillPane = ({ billId: billIdProp }: IBillPaneProps = {}) => {
  const params = useParams();
  const billId = billIdProp ?? params.billId ?? '';
  const closeRecord = useCloseRecord(PATHS.OBLIGATIONS);
  const showDebugJson = useFeatureGate(FeatureGate.DebugTransactionDetails);

  const { data, loading, error } = useQuery<GetBillPageQuery, GetBillPageQueryVariables>(GET_BILL_PAGE, {
    variables: { id: billId, includeRawJson: showDebugJson },
    skip: !billId,
  });
  const bill = data?.getBill;
  const status = billRecordStatus({ loading, error, bill });

  return (
    <RecordPane
      status={status}
      onClose={closeRecord}
      hero={bill ? <BillHero bill={bill} onClose={closeRecord} /> : undefined}
      footer={bill?.senderIdentifier ?? undefined}
      notFoundMessage={billId ? "This bill isn't available." : 'No bill was specified.'}
    >
      {bill && <BillBody bill={bill} />}
      {showDebugJson && bill?.rawJson && (
        <RecordPane.Section title="Debug">
          <JsonDebugRow label="Bill JSON" rawJson={bill.rawJson} />
        </RecordPane.Section>
      )}
    </RecordPane>
  );
};

interface IBillHeroProps {
  bill: BillPageBill;
  onClose: () => void;
}

/**
 * The name follows `billDisplayName`, so a card statement with no merchant
 * reads as its account; the avatar follows the same precedence and draws the
 * bank's logo in that case.
 */
const BillHero = ({ bill, onClose }: IBillHeroProps) => {
  const logoEntity = bill.merchant ?? bill.financialAccount ?? null;
  const timing = getTimingPill(bill.dueDate, { status: bill.status });
  const meta: IRecordHeroMetaCell[] = [{ label: 'Date', value: billDateCellValue(bill), icon: <CalendarIcon /> }];
  if (bill.financialAccount) {
    const account = bill.financialAccount;
    meta.push({
      label: 'Account',
      value: formatAccountLabel(account),
      leading: <Avatar size="sm" shape="square" src={bankLogoSrc(account.bank)} alt={account.name} />,
    });
  }

  return (
    <RecordPane.Hero
      statusPill={
        <Badge type="pill-color" size="lg" color={timing.color}>
          {timing.label}
        </Badge>
      }
      logoSrc={logoEntity ? entityLogoSrc(logoEntity) : null}
      name={billDisplayName(bill)}
      showRecurringBadge={bill.recurringTransaction !== null}
      amount={toCurrency(Math.abs(Number(bill.amountDue)), { currencyCode: bill.currencyCode })}
      meta={meta}
      onClose={onClose}
    />
  );
};

interface IBillBodyProps {
  bill: BillPageBill;
}

/**
 * Spending compares one debit against the merchant's others. A bill enters
 * that aggregate only once paid, through its payments: an unpaid or partially
 * paid bill is compared from outside it as its billed amount, a bill settled
 * by one payment stands in for that payment at the payment's own amount (which
 * can exceed the bill), and a bill settled by several has no single debit to
 * compare, so the section is left out.
 */
const BillBody = ({ bill }: IBillBodyProps) => {
  const merchantId = bill.merchant?.id ?? null;
  const paymentsInStats = bill.status === BillStatus.Paid ? billPaymentsAtMerchant(bill) : [];
  const solePayment = paymentsInStats.length === 1 ? paymentsInStats[0] : null;

  return (
    <>
      <RecordAlertBanner workItem={bill.workItem} />
      {bill.recurringTransaction ? (
        <RecurringStreamSection recurringTransaction={bill.recurringTransaction} amount={bill.amountDue} />
      ) : (
        paymentsInStats.length <= 1 && (
          <MerchantSpendingSection
            merchantId={merchantId}
            amount={solePayment?.amount ?? bill.amountDue}
            currentInStats={solePayment !== null}
          />
        )
      )}
      <AttachmentsSection bill={bill} />
      <RecentTransactionsSection merchantId={merchantId} />
    </>
  );
};

export default BillPane;
